import { Alignment, ArrayUtil, ModelRow } from "@mcleod/core";
import { AutogenLayoutMultiReprintFreightBill } from "./autogen/AutogenLayoutMultiReprintFreightBill";
import { Button, Layout, ReportData, ReportPreview, ReportPreviewTabSource, SlideoutDecorator, Snackbar, Tab } from "@mcleod/components";
import { InvoiceProcessingExceptionTable } from "./reports/InvoiceProcessingExceptionTable";
import { ImageName } from "@mcleod/images";

interface MultiReprintFreightBillProps {
    openItems: ModelRow[];
    includeOnlyInvoices: boolean;
    invoiceNumberFieldName: string;
    isBalanceDue: boolean;
    sendToImaging: boolean;
}

export class MultiReprintFreightBill extends AutogenLayoutMultiReprintFreightBill {
    private reportPreview: ReportPreview;
    private _buttonConfirm = new Button({
        id: "buttonConfirm",
        caption: "Close",
        backgroundColor: "primary",
        color: "primary.reverse",
        minWidth: 128,
        borderWidth: 0,
        rowBreak: false,
        enabled: true,
        onClick: () => {
            this.slideOut();
        }
    });

    override onLoad() {
        this.createReportPreview();
    }

    private createReportPreview() {
        this.reportPreview = new ReportPreview(
            { reportPreviewTitle: MultiReprintFreightBill.getTitle(false) },
            { id: "reportPreview" });
        this.add(this.reportPreview);
    }

    public static openMultiReprintSlideout(props: MultiReprintFreightBillProps) {
        const multiReprintFreightBillLayout = Layout.getLayout("lme/ar/MultiReprintFreightBill") as MultiReprintFreightBill;
        new SlideoutDecorator({
            layout: multiReprintFreightBillLayout,
            fillVerticalSpace: true,
            width: "100%",
            title: MultiReprintFreightBill.getTitle(props.isBalanceDue),
            addlComponents: [multiReprintFreightBillLayout.buttonConfirm],
            transitionOptions: {
                direction: Alignment.BOTTOM
            },
            doAfterSlideIn: async () => {
                multiReprintFreightBillLayout.reportPreview.reportName = MultiReprintFreightBill.getTitle(props.isBalanceDue);
                multiReprintFreightBillLayout.buttonConfirm.busy = true;
                await multiReprintFreightBillLayout.sourceMultiReprintFreightBill.search({
                    open_items: props.openItems,
                    include_only_invoices: props.includeOnlyInvoices,
                    invoice_number_field_name: props.invoiceNumberFieldName,
                    is_balance_due: props.isBalanceDue,
                    send_to_imaging: props.sendToImaging
                });
                multiReprintFreightBillLayout.buttonConfirm.busy = false;
            }
        });
    }

    sourceMultiReprintFreightBillAfterExecution() {
        this.displayReportData();
    }

    private displayReportData() {
        this.reportPreview.beginSearch();

        const row = ArrayUtil.getFirstElement(this.sourceMultiReprintFreightBill.data) as ModelRow;
        const freightBillReportData = this.createReportDataFromFields(row);
        const exceptionTab = this.createExceptionTab(row?.get("exception_list", []));
        const tabSources = [
            freightBillReportData,
            exceptionTab
        ] as ReportPreviewTabSource[];

        this.reportPreview.endSearch(this.cleanTabSources(tabSources));
        if (this.reportPreview.isEmpty()) {
            Snackbar.showWarningSnackbar("No bills to print");
        }
    }

    private createReportDataFromFields(row: ModelRow): ReportData {
        const reports = row?.get("reports", []);
        if (!ArrayUtil.isEmptyArray(reports)) {
            const reportList = Object.values(reports).map((report: any) => {
                return new ReportData(report.report_name, {
                    excelBytes: report.excel_output,
                    hasExceptions: report.has_exceptions,
                    pdfBytes: report.pdf_output,
                    reportName: report.report_name,
                    reportType: report.report_type,
                    serverError: report.error_info
                });
            });

            // Only one report should be generated, so just return the first one
            return ArrayUtil.getFirstElement(reportList);
        }
        return null;
    }

    private createExceptionTab(exceptionList: any[]): Tab {
        if (ArrayUtil.isEmptyArray(exceptionList)) {
            return null;
        }
        const layoutExceptionTable =
            Layout.getLayout("lme/ar/reports/InvoiceProcessingExceptionTable") as InvoiceProcessingExceptionTable;
        layoutExceptionTable.addLayoutLoadListener(() => {
            layoutExceptionTable.data = exceptionList;
        });
        const tab = new Tab({ components: [ layoutExceptionTable ], caption: "Exceptions"});
        tab.imageName = ImageName.triangleWarning;
        tab.imageColor = "error";
        tab.headingImage.rowBreak = false;
        return tab;
    }

    private cleanTabSources(tabSources: ReportPreviewTabSource[]): ReportPreviewTabSource[] {
        return tabSources.filter(data => data != null && !data.isEmpty());
    }

    public get buttonConfirm(): Button {
        return this._buttonConfirm;
    }

    private static getTitle(isBalanceDue: boolean): string {
        return isBalanceDue ? "Reprint Balance Due" : "Reprint Freight Bill";
    }
}
